import { Link } from "react-router-dom";
import { C, font } from "../styles/tokens";

/**
 * EmptyState - a friendly placeholder for sections with no data yet.
 *
 * Three sizes:
 *   compact - small icon, single line, used inside cards
 *   medium  - default, medium icon + heading + body + optional CTA
 *   large   - dedicated empty-page treatment
 *
 * The icon picks from a set of friendly SVGs (folder, file, etc.) based on
 * `kind` - keep it simple, no external illustrations.
 */
export function EmptyState({
  kind = "folder",
  title,
  body,
  ctaLabel,
  ctaTo,
  onCtaClick,
  size = "medium",
}) {
  const padding =
    size === "compact"
      ? "20px 16px"
      : size === "large"
        ? "48px 24px"
        : "32px 24px";
  const iconSize = size === "compact" ? 32 : size === "large" ? 64 : 48;
  const titleSize = size === "compact" ? 14 : size === "large" ? 18 : 15;

  return (
    <div
      style={{
        padding,
        textAlign: "center",
        fontFamily: font,
      }}
    >
      <div style={{ marginBottom: 12, opacity: 0.55 }}>
        <Icon kind={kind} size={iconSize} />
      </div>
      <div
        style={{
          fontSize: titleSize,
          fontWeight: 700,
          color: C.textDark,
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      {body && (
        <div
          style={{
            fontSize: 13,
            color: C.textMed,
            lineHeight: 1.5,
            marginBottom: ctaLabel ? 16 : 0,
            maxWidth: 400,
            margin: ctaLabel ? "0 auto 16px" : "0 auto",
          }}
        >
          {body}
        </div>
      )}
      {ctaLabel &&
        (ctaTo ? (
          <Link to={ctaTo} style={primaryButtonStyle}>
            {ctaLabel}
          </Link>
        ) : (
          <button onClick={onCtaClick} style={primaryButtonStyle}>
            {ctaLabel}
          </button>
        ))}
    </div>
  );
}

function Icon({ kind, size }) {
  const stroke = C.textLight;
  const strokeWidth = 1.5;
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke,
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (kind) {
    case "folder":
      return (
        <svg {...common}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "file":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...common}>
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
          <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
      );
    case "check":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    default:
      return null;
  }
}

const primaryButtonStyle = {
  display: "inline-block",
  padding: "10px 22px",
  fontSize: 13,
  fontWeight: 600,
  color: "white",
  background: C.blue,
  border: 0,
  borderRadius: 4,
  cursor: "pointer",
  textDecoration: "none",
  fontFamily: font,
};
